import React from "react";
import { cn } from "utils/utils";
import { Package, Search } from "lucide-react";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";
import MatchedCardsIcon from "modules/common/components/Icons/MatchedCardsIcon";

interface TradeSectionTitleSkeletonProps {
  title: string;
  className?: string;
  type?: "wants" | "trades" | "match";
  showMobileFilterBar?: boolean;
  /** Mirror TradeSectionTitle's `countInMainbar`: the real row collapses on
   *  desktop (count lives in the page mainbar) and uses `py-2`, so the
   *  skeleton must too or the grid jumps when content swaps in. */
  countInMainbar?: boolean;
}

function TradeSectionTitleSkeleton({
  title,
  className,
  type,
  showMobileFilterBar = false,
  countInMainbar = false,
}: TradeSectionTitleSkeletonProps) {
  const icon = () => {
    switch (type) {
      case "wants":
        return <Search size={ICON_SIZE} />;
      case "trades":
        return <Package size={ICON_SIZE} />;
      case "match":
        return <MatchedCardsIcon fontSize={ICON_SIZE} />;
      default:
        return null;
    }
  };

  return (
    <div
      className={cn(
        // py-2 matches the real TradeSectionTitle's row box.
        "flex w-full items-center justify-between py-2",
        countInMainbar && "md:hidden",
        className
      )}
    >
      {/* Left side: title OR mobile filter bar */}
      {showMobileFilterBar ? (
        <>
          {/* Mobile: Filter bar skeleton (Search + Sort chips) */}
          <div className="no-scrollbar flex gap-2 overflow-x-auto md:hidden">
            <div className="skeleton btn btn-sm btn-soft border-base-300 h-8 w-20 rounded-full border" />
            <div className="skeleton btn btn-sm btn-soft border-base-300 h-8 w-16 rounded-full border" />
          </div>
          {/* Desktop: Title */}
          <h2 className="text-base-content hidden items-center gap-3 text-balance text-lg font-semibold md:flex md:text-xl">
            {title}
          </h2>
        </>
      ) : (
        <h2 className="text-base-content flex items-start gap-3 text-balance text-lg font-semibold md:items-center md:text-xl">
          {title}
        </h2>
      )}

      {/* TradeCountBadge returns null for type="match" (no true total), even
          while loading — so the matched skeleton must not show one either. */}
      {type !== "match" && (
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "badge badge-soft badge-md items-center gap-1 md:gap-2",
              type === "trades" && "badge-warning",
              type === "wants" && "badge-secondary",
              !type && "badge-ghost"
            )}
          >
            {icon()}
            <div className="skeleton h-4 w-8"></div>
          </span>
        </div>
      )}
    </div>
  );
}

export default TradeSectionTitleSkeleton;
